import React, { useState, useEffect, useRef } from 'react';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import { styled } from '@mui/system';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  IconButton,
  Chip,
  Paper,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Person,
  Logout,
  Settings,
  AccountCircle,
  Email,
  Business,
  Security,
  Notifications,
  Warning,
  CheckCircle,
  Error,
  Info,
  Close,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../../common/functions/log_out.function';
import { notify } from '../../common/functions/react-toastify.function';
import { getPermissions } from '../../common/functions/get_permissions_from_storage.function';
import {
  hasAnyPermission,
  hasNetworkMonitoringPermissions,
  NETWORK_MONITORING_PERMISSIONS,
  INVENTORY_MANAGEMENT_PERMISSIONS,
  USER_MANAGEMENT_PERMISSIONS,
  PARTNER_NETWORK_PERMISSIONS,
  KMZ_PERMISSIONS,
  RING_PLANNING_PERMISSIONS,
  FIBER_POP_LIST_PERMISSIONS,
} from '../../common/functions/permission_utils.function';

const ProfilePopup = ({ anchor, setAnchor, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [showAllPermissions, setShowAllPermissions] = useState(false);
  const [isTogglingPermissions, setIsTogglingPermissions] = useState(false);
  const open = Boolean(anchor);
  const profileOpen = Boolean(showProfile);
  const id = open ? 'profile-popup' : undefined;
  const profileId = profileOpen ? 'profile-detail-popup' : undefined;
  const popupRef = useRef(null);
  const profilePopupRef = useRef(null);

  // Get user data from sessionStorage
  const loginState = sessionStorage.getItem('logInState');
  const tenantId = sessionStorage.getItem('tenantId');
  const tenantName = sessionStorage.getItem('tenantName');
  const selectedTab = sessionStorage.getItem('selectedTab');
  const totalFttsAlarms = sessionStorage.getItem('totalFttsAlarms') || '0';
  const totalIpranAlarms = sessionStorage.getItem('totalIpranAlarms') || '0';
  const totalMpbnAlarms = sessionStorage.getItem('totalMpbnAlarms') || '0';
  const totalOtnAlarms = sessionStorage.getItem('totalOtnAlarms') || '0';

  // Parse JSON strings
  const userName = loginState ? JSON.parse(loginState) : 'User';
  const tenantNameParsed = tenantName ? JSON.parse(tenantName) : 'Tenant';

  // Get user permissions
  const permissions = getPermissions();
  const permissionNames = Array.isArray(permissions)
    ? permissions.map((permission) => permission.name)
    : [];

  // Handle permission toggle with animation
  const handlePermissionToggle = () => {
    setIsTogglingPermissions(true);
    setTimeout(() => {
      setShowAllPermissions(!showAllPermissions);
      setIsTogglingPermissions(false);
    }, 150);
  };

  // Function to check if user has permission for current path
  const hasPermissionForPath = (path) => {
    if (path.includes('/userManagement')) {
      return hasAnyPermission(permissionNames, USER_MANAGEMENT_PERMISSIONS);
    } else if (path.includes('/fiberSite') || path.includes('/fiberSitePlanning')) {
      return hasAnyPermission(permissionNames, ['fiberSite']);
    } else if (path.includes('/randb') || path.includes('/ranDb')) {
      return hasAnyPermission(permissionNames, ['ranDb']);
    } else if (path.includes('/tenantManagement')) {
      return hasAnyPermission(permissionNames, USER_MANAGEMENT_PERMISSIONS);
    } else if (path.includes('/rov')) {
      return true; // Dashboard is accessible to all
    } else if (path.includes('/networkAlarms') || path.includes('/network_alarms')) {
      return hasAnyPermission(permissionNames, ['Network Alarms']);
    } else if (path.includes('/fibers')) {
      return hasNetworkMonitoringPermissions(permissionNames);
    } else if (path.includes('/networkLayer')) {
      return hasAnyPermission(permissionNames, ['Network Layer Visualization']);
    } else if (path.includes('/inventory')) {
      return hasAnyPermission(permissionNames, ['Inventory']);
    } else if (path.includes('/leasedFiber')) {
      return hasAnyPermission(permissionNames, ['Leased Fiber Pair']);
    } else if (path.includes('/networkPlanning/kmzPlanning')) {
      return hasAnyPermission(permissionNames, KMZ_PERMISSIONS);
    } else if (path.includes('/kmzMap') || path.includes('/kmz')) {
      return hasAnyPermission(permissionNames, PARTNER_NETWORK_PERMISSIONS);
    } else if (path.includes('/networkPlanning/ringPlanning')) {
      return hasAnyPermission(permissionNames, RING_PLANNING_PERMISSIONS);
    } else if (path.includes('/networkPlanning/fiberPopList')) {
      return hasAnyPermission(permissionNames, FIBER_POP_LIST_PERMISSIONS);
    } else if (path.includes('/invoiceAttachmentManagement')) {
      return permissionNames?.includes('Invoice Attachment Management');
    } else if (
      path.includes('/purchaseOrderManagement') ||
      path.includes('/poWorkflowManagement')
    ) {
      return permissionNames?.includes('Admin Invoice Management');
    }

    return false;
  };

  // Function to get current module info (same logic as header bar)
  const getCurrentModuleInfo = () => {
    const path = location.pathname;

    // Check if user has permission for current path
    if (!hasPermissionForPath(path)) {
      return { moduleName: 'Dashboard', subModuleName: null };
    }

    if (path.includes('/userManagement')) {
      return { moduleName: 'Access Control', subModuleName: null };
    } else if (path.includes('/fiberSite') || path.includes('/fiberSitePlanning')) {
      return { moduleName: 'Inventory Management', subModuleName: 'FTTS Site List' };
    } else if (path.includes('/randb') || path.includes('/ranDb')) {
      return { moduleName: 'Inventory Management', subModuleName: 'RAN DB' };
    } else if (path.includes('/tenantManagement')) {
      return { moduleName: 'Tenant Management', subModuleName: null };
    } else if (path.includes('/rov')) {
      return { moduleName: 'Dashboard', subModuleName: null };
    } else if (path.includes('/networkAlarms') || path.includes('/network_alarms')) {
      return { moduleName: 'Network Alarms', subModuleName: null };
    } else if (path.includes('/fibers')) {
      // For Network Monitoring, use selectedTab to determine sub-module
      const currentTab = selectedTab || 'ftts';
      const subModuleMap = {
        ftts: 'FTTS',
        mpbn: 'MPBN',
        otn: 'OTN',
        ipran: 'IPRAN',
      };
      return {
        moduleName: 'Network Monitoring',
        subModuleName: subModuleMap[currentTab] || 'FTTS',
      };
    } else if (path.includes('/networkLayer')) {
      return { moduleName: 'Inventory Management', subModuleName: 'Network Layer Visualization' };
    } else if (path.includes('/inventory')) {
      return { moduleName: 'Inventory Management', subModuleName: 'OFC Inventory' };
    } else if (path.includes('/leasedFiber')) {
      return { moduleName: 'Inventory Management', subModuleName: 'Leased Fiber Pair' };
    } else if (path.includes('/networkPlanning/kmzPlanning')) {
      return { moduleName: 'Network Planning', subModuleName: 'KMZ' };
    } else if (path.includes('/kmzMap') || path.includes('/kmz')) {
      return { moduleName: 'Partner Network', subModuleName: null };
    } else if (path.includes('/networkPlanning/ringPlanning')) {
      return { moduleName: 'Network Planning', subModuleName: 'Ring Planning' };
    } else if (path.includes('/purchaseOrderManagement')) {
      return { moduleName: 'Invoice Management', subModuleName: 'Purchase Order Management' };
    } else if (path.includes('/poWorkflowManagement')) {
      return { moduleName: 'Invoice Management', subModuleName: 'Vendor Invoice Management' };
    } else if (path.includes('/invoiceAttachmentManagement')) {
      return {
        moduleName: 'Invoice Management',
        subModuleName: 'Invoice Attachment Management',
      };
    }

    return { moduleName: 'Dashboard', subModuleName: null };
  };

  const { moduleName, subModuleName } = getCurrentModuleInfo();
  const displayModuleName =
    moduleName === 'Partner Network' ? 'Comprehensive Route Planning' : moduleName;
  const displaySubModuleName =
    subModuleName === 'KMZ' ? 'KMZ Upload with Network Overlay' : subModuleName;
  const currentModuleDisplay = displaySubModuleName
    ? `${displayModuleName} - ${displaySubModuleName}`
    : displayModuleName;

  // Click outside to close functionality
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle main popup
      if (open && popupRef.current && !popupRef.current.contains(event.target)) {
        if (anchor && !anchor.contains(event.target)) {
          onClose();
        }
      }

      // Handle profile popup
      if (
        profileOpen &&
        profilePopupRef.current &&
        !profilePopupRef.current.contains(event.target)
      ) {
        if (anchor && !anchor.contains(event.target)) {
          handleProfileClose();
        }
      }
    };

    if (open || profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, profileOpen, anchor, onClose]);

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      const result = await logout();
      navigate('/');
      notify('success', result.message);
    } catch (error) {
      notify('error', error?.response?.data?.message);
      navigate('/');
    }
    setIsLoading(false);
  };

  const handleProfileClick = () => {
    // Close the main popup and open profile popup as centered modal
    onClose();
    setShowProfile(true);
  };

  const handleProfileClose = () => {
    setShowProfile(false);
  };

  const handleBackClick = () => {
    // Simply close the profile popup - no need to reopen main popup
    handleProfileClose();
  };

  const handleSettingsClick = () => {
    // Navigate to settings page
    console.log('Navigate to settings page');
    onClose();
  };

  // Profile View Component
  const ProfileView = () => (
    <>
      {/* Header with Back Button */}
      <Box
        sx={{
          background: 'black',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
        }}
      >
        <IconButton
          onClick={handleBackClick}
          sx={{
            color: 'white',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 700,
            flex: 1,
          }}
        >
          User Profile
        </Typography>
        <IconButton
          onClick={handleProfileClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Profile Content */}
      <Box sx={{ p: 3, pr: 4 }}>
        {/* User Info Card */}
        <Card
          sx={{
            mb: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mr: 2,
                }}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  {userName}
                </Typography>
                <Chip
                  label="Super Admin"
                  size="small"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Business sx={{ color: 'white', mr: 1, fontSize: '1.2rem' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Organization
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    {tenantNameParsed}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Info sx={{ color: 'white', mr: 1, fontSize: '1.2rem' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Current Module
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    {currentModuleDisplay}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Alarms Summary Card */}
        <Card
          sx={{
            mb: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Notifications sx={{ color: '#ff6b6b', mr: 1, fontSize: '1.5rem' }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                Active Alarms
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  p: 1.5,
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '80px',
                  flex: 1,
                }}
              >
                <Warning sx={{ color: 'white', fontSize: '1.5rem', mb: 0.5 }} />
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                  {totalFttsAlarms}
                </Typography>
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                  FTTS
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '80px',
                  flex: 1,
                }}
              >
                <Error sx={{ color: 'white', fontSize: '1.5rem', mb: 0.5 }} />
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                  {totalIpranAlarms}
                </Typography>
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                  IPRAN
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '80px',
                  flex: 1,
                }}
              >
                <Notifications sx={{ color: 'white', fontSize: '1.5rem', mb: 0.5 }} />
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                  {totalMpbnAlarms}
                </Typography>
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                  MPBN
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '80px',
                  flex: 1,
                }}
              >
                <CheckCircle sx={{ color: 'white', fontSize: '1.5rem', mb: 0.5 }} />
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                  {totalOtnAlarms}
                </Typography>
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                  OTN
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2, p: 2, background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center' }}
              >
                Total Active Alarms:{' '}
                {parseInt(totalFttsAlarms) +
                  parseInt(totalIpranAlarms) +
                  parseInt(totalMpbnAlarms) +
                  parseInt(totalOtnAlarms)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Permissions Summary */}
        <Card
          sx={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Security
                sx={{
                  color: 'white',
                  mr: 1,
                  fontSize: '1.5rem',
                  transition: 'all 0.3s ease',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                    },
                  },
                }}
              />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                Access Permissions
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {/* Always show first 6 permissions */}
              {permissionNames.slice(0, 6).map((permission, index) => (
                <Chip
                  key={index}
                  label={permission}
                  size="small"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 500,
                    // animation: 'fadeInUp 0.3s ease-out',
                    animationDelay: `${index * 0.1}s`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    '@keyframes fadeInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(10px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px) scale(1.02)',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              ))}

              {/* + More button positioned right after the first 6 permissions */}
              {permissionNames.length > 6 && !showAllPermissions && (
                <Tooltip
                  title={`Show ${permissionNames.length - 6} more permissions`}
                  arrow
                  placement="top"
                >
                  <Chip
                    label={isTogglingPermissions ? '...' : `+${permissionNames.length - 6} more`}
                    size="small"
                    onClick={handlePermissionToggle}
                    disabled={isTogglingPermissions}
                    sx={{
                      background: isTogglingPermissions
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 500,
                      cursor: isTogglingPermissions ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      opacity: isTogglingPermissions ? 0.7 : 1,
                      '&:hover': {
                        background: isTogglingPermissions
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                        transform: 'scale(1.05)',
                        boxShadow: isTogglingPermissions
                          ? 'none'
                          : '0 4px 12px rgba(102, 126, 234, 0.4)',
                      },
                      '&:active': {
                        transform: 'scale(0.95)',
                      },
                    }}
                  />
                </Tooltip>
              )}

              {/* Additional permissions that appear on the same line when expanded */}
              {permissionNames.length > 6 && showAllPermissions && (
                <>
                  {permissionNames.slice(6).map((permission, index) => (
                    <Chip
                      key={index + 6}
                      label={permission}
                      size="small"
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 500,
                        animation: 'slideInFromTop 0.2s ease-out',
                        animationDelay: `${index * 0.05}s`,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '@keyframes slideInFromTop': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateY(-10px) scale(0.9)',
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateY(0) scale(1)',
                          },
                        },
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.3)',
                          transform: 'translateY(-2px) scale(1.02)',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        },
                      }}
                    />
                  ))}

                  {/* Show Less button positioned at the end of all permissions */}
                  <Tooltip title="Show less permissions" arrow placement="top">
                    <Chip
                      label={isTogglingPermissions ? '...' : 'Show Less'}
                      size="small"
                      onClick={handlePermissionToggle}
                      disabled={isTogglingPermissions}
                      sx={{
                        background: isTogglingPermissions
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: 500,
                        cursor: isTogglingPermissions ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: isTogglingPermissions ? 0.7 : 1,
                        '&:hover': {
                          background: isTogglingPermissions
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          transform: 'scale(1.05)',
                          boxShadow: isTogglingPermissions
                            ? 'none'
                            : '0 4px 12px rgba(102, 126, 234, 0.4)',
                        },
                        '&:active': {
                          transform: 'scale(0.95)',
                        },
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );

  // Main Popup View
  const MainPopupView = () => (
    <Box
      sx={{
        width: 320,
        borderRadius: '16px',
        // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        background: '#1b1919',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Header Section - Row Layout */}
      <Box
        sx={{
          background: 'black',
          p: 2,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          }}
        />
        <Avatar
          sx={{
            width: 50,
            height: 50,
            border: '3px solid rgba(255, 255, 255, 0.3)',
            background: 'linear-gradient(45deg, #f2a2a7 0%, #bd1d23 100%)',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            flexShrink: 0,
          }}
        >
          {userName.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {userName}
          </Typography>
        </Box>
      </Box>

      {/* User Info Section */}
      <Box sx={{ p: 2, background: 'rgba(255, 255, 255, 0.05)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Business sx={{ color: 'white', mr: 1.5 }} />
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Organization
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
              {tenantNameParsed}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Security sx={{ color: 'white', mr: 1.5 }} />
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Current Module
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'white', fontWeight: 600, textTransform: 'uppercase' }}
            >
              {currentModuleDisplay}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* Action Buttons */}
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          startIcon={<Person />}
          onClick={handleProfileClick}
          sx={{
            mb: 1,
            background: '#f2a2a7',
            color: 'white',
            borderRadius: '12px',
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #e085e8 0%, #e54b5f 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(240, 147, 251, 0.4)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          View Profile
        </Button>

        {/* Settings Button - Commented out for now
        <Button
          fullWidth
          startIcon={<Settings />}
          onClick={handleSettingsClick}
          sx={{
            mb: 1,
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: '12px',
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #e085e8 0%, #e54b5f 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(240, 147, 251, 0.4)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Settings
        </Button>
        */}

        <Button
          fullWidth
          startIcon={<Logout />}
          onClick={handleLogout}
          disabled={isLoading}
          sx={{
            background: '#bd1d23',
            color: 'white',
            borderRadius: '12px',
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #ff5252 0%, #e64a19 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
            },
            '&:disabled': {
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {isLoading ? 'Logging out...' : 'Logout'}
        </Button>
      </Box>

      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            color: 'white',
            background: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <AccountCircle />
      </IconButton>
    </Box>
  );

  return (
    <div>
      {/* Main Profile Popup */}
      <BasePopup id={id} open={open} anchor={anchor} style={{ zIndex: 2000 }}>
        <PopupBody ref={popupRef} data-popup="profile-popup">
          <MainPopupView />
        </PopupBody>
      </BasePopup>

      {/* Profile Detail Popup */}
      {profileOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2001,
          }}
        >
          <Box
            ref={profilePopupRef}
            sx={{
              width: { xs: '95%', sm: '90%', md: '800px', lg: '900px' },
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: '16px',
              // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              background: '#1b1919',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              // Custom scrollbar styling
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.5)',
                },
              },
              // Firefox scrollbar styling
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)',
            }}
          >
            <ProfileView />
          </Box>
        </Box>
      )}
    </div>
  );
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};
// margin: 8px;
//   border-radius: 8px;
//   border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
//   background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
//   box-shadow: ${
//     theme.palette.mode === 'dark' ? `0px 4px 8px rgb(0 0 0 / 0.7)` : `0px 4px 8px rgb(0 0 0 / 0.1)`
//   };

const PopupBody = styled('div')(
  ({ theme }) => `
  width: max-content;
  padding: 12px 16px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  z-index: 1;
`
);

export default ProfilePopup;
